import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditPricingPopup from "./EditPricingPopup";
import AddPricingPopup from "./AddPricingPopup";
import axiosInstance from "../../../axiosConfig";

function Pricing() {
  const [modules, setModules] = useState([]);
  const [pricingPlans, setPricingPlans] = useState({});
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [viewMode, setViewMode] = useState("product");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const editPopupRef = useRef(null);

  useEffect(() => {
    const sp = localStorage.getItem("selectedProduct");
    if (sp) {
      try {
        setSelectedProduct(JSON.parse(sp));
      } catch {
        toast.error("Invalid product data");
      }
    }
  }, []);

  const fetchModules = useCallback(async () => {
    if (!selectedProduct) return;
    setLoading(true);
    try {
      const endpoint =
        viewMode === "product" && selectedProduct.productId
          ? `/modules/${selectedProduct.productId}/products`
          : "/modules";
      const res = await axiosInstance.get(endpoint);
      const raw = res.data?.data;
      setModules(Array.isArray(raw) ? raw : raw ? [raw] : []);
    } catch {
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedProduct]);

  const fetchPricing = useCallback(async () => {
    if (!selectedProduct) return;

    try {
      const res = await axiosInstance.get(
        `/module-pricing-plans/${selectedProduct.productId}/products`,
      );

      const raw = res.data?.data;
      const dataArray = Array.isArray(raw) ? raw : raw ? [raw] : [];

      // Map the flat array into a moduleId-keyed object
      const pricingData = {};
      for (const m of modules) {
        pricingData[m.moduleId] = [];
      }
      for (const item of dataArray) {
        if (item.moduleId !== undefined) {
          pricingData[item.moduleId] = [item];
        }
      }

      setPricingPlans(pricingData);
    } catch {
      toast.error("Failed to load pricing");
    }
  }, [modules, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) fetchModules();
  }, [fetchModules, selectedProduct]);

  useEffect(() => {
    if (modules.length > 0) fetchPricing();
  }, [modules, fetchPricing]);

  const handleEdit = (moduleId) => {
    setSelectedModuleId(moduleId);
    setTimeout(
      () => editPopupRef.current?.scrollIntoView({ behavior: "smooth" }),
      50,
    );
  };

  const closePopups = () => {
    setSelectedModuleId(null);
    setIsAddPopupOpen(false);
  };

  const getGroupedPricingForModule = (moduleId) => {
    const pricingArray = pricingPlans[moduleId] || [];
    return pricingArray.reduce((acc, pricingObj) => {
      (pricingObj.subscriptionPlans || []).forEach((sub) => {
        const {
          planName,
          moduleFeatures = [],
          featuresAccessIds = [],
          userPlans = [],
        } = sub;
        if (!acc[planName]) acc[planName] = [];
        const consolidatedFeatures = moduleFeatures.map((feature) => ({
          ...feature,
          accessCodes: featuresAccessIds
            .filter(
              (a) => a.featureName === feature.featureName || !a.featureName,
            )
            .map((a) => a.featureAccessCode),
        }));
        acc[planName].push({
          ...sub,
          features: consolidatedFeatures,
          orphanAccessCodes: featuresAccessIds
            .filter((a) => !a.featureName)
            .map((a) => a.featureAccessCode),
          userPlans,
          featuresAccessIds,
        });
      });
      return acc;
    }, {});
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-2 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Pricing Management
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage module pricing plans and feature access
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Product badge */}
            {selectedProduct && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <svg
                  className="w-4 h-4 text-blue-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <span className="text-sm font-semibold text-blue-700">
                  {selectedProduct.productName}
                </span>
              </div>
            )}

            {/* View toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("product")}
                className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 ${
                  viewMode === "product"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                Product View
              </button>
              <button
                onClick={() => setViewMode("all")}
                className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 ${
                  viewMode === "all"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                All Pricing
              </button>
            </div>

            {/* Add button */}
            <button
              onClick={() => setIsAddPopupOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 px-5 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Pricing
            </button>
          </div>
        </div>

        {/* CONTENT */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-base text-gray-500">
                Loading pricing...
              </span>
            </div>
          </div>
        ) : modules.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200">
            <svg
              className="w-12 h-12 text-gray-300 mx-auto mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <p className="text-base font-medium text-gray-600">
              No modules found
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {viewMode === "product"
                ? `No modules for ${selectedProduct?.productName}`
                : "No modules available"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {modules.map((module) => {
              const groupedPricing = getGroupedPricingForModule(
                module.moduleId,
              );
              const planCount = Object.keys(groupedPricing).length;

              return (
                <div
                  key={module.moduleId}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  {/* Module Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3.5 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-white">
                        {module.moduleName}
                      </h3>
                      {planCount > 0 && (
                        <span className="bg-white/20 text-white text-xs px-2.5 py-0.5 rounded-full">
                          {planCount} plan{planCount > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    {planCount > 0 && (
                      <button
                        onClick={() => handleEdit(module.moduleId)}
                        className="bg-white/20 hover:bg-white/30 text-white text-sm font-medium py-1.5 px-4 rounded-md transition-colors"
                      >
                        Edit Pricing
                      </button>
                    )}
                  </div>

                  {/* Pricing Body */}
                  <div className="p-4">
                    {planCount === 0 ? (
                      <p className="text-sm text-gray-400 italic">
                        No pricing plans configured for this module
                      </p>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(groupedPricing).map(
                          ([planName, plans]) => (
                            <div
                              key={planName}
                              className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                            >
                              {/* Plan Name */}
                              <h4 className="text-sm font-bold text-gray-800 tracking-wide mb-3 pb-2 border-b border-gray-200">
                                {planName}
                              </h4>

                              {/* User Ranges */}
                              {(plans[0].userPlans || []).length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-semibold text-gray-500 tracking-wide mb-1.5">
                                    User Ranges
                                  </p>
                                  <div className="space-y-1.5">
                                    {plans[0].userPlans.map((u, i) => (
                                      <div
                                        key={i}
                                        className="flex justify-between items-center bg-white px-3 py-1.5 rounded-lg border border-gray-100"
                                      >
                                        <span className="text-sm text-gray-500">
                                          {u.minUser}–{u.maxUser} users
                                        </span>
                                        <span className="text-sm font-bold text-green-600">
                                          ₹{u.planAmount}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Features */}
                              {(plans[0].features || []).length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-semibold text-gray-500 tracking-wide mb-1.5">
                                    Features
                                  </p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {plans[0].features.map((f, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center gap-1.5"
                                      >
                                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                                        <span
                                          className="text-sm text-gray-600 truncate"
                                          title={f.featureName}
                                        >
                                          {f.featureName}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Access Codes */}
                              {(plans[0].featuresAccessIds || []).length >
                                0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 tracking-wide mb-1.5">
                                    Access Codes
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {plans[0].featuresAccessIds.map(
                                      (access, idx) => (
                                        <span
                                          key={idx}
                                          className="px-2 py-0.5 rounded text-xs font-mono font-medium bg-purple-100 text-purple-700"
                                        >
                                          {access.featureAccessCode}
                                        </span>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit Popup */}
        {selectedModuleId && (
          <div ref={editPopupRef}>
            <EditPricingPopup
              moduleId={selectedModuleId}
              onClose={closePopups}
              refreshPricingPlans={fetchPricing}
            />
          </div>
        )}

        {/* Add Popup */}
        {isAddPopupOpen && (
          <AddPricingPopup
            onClose={closePopups}
            modules={modules}
            refreshPricingPlans={fetchPricing}
          />
        )}
      </div>
    </div>
  );
}

export default Pricing;
